import { useState } from "react";
import { post, put } from "@/shared/utils/fetchApi";
import { logger } from "@/infrastructure/lib/logger";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useTranslation } from "react-i18next";
import { finishInvitedOnboarding } from "../utils/finishInvitedOnboarding";
import type { OnboardingStep } from "../types/onboarding-types";

interface ProfileData {
  first_name: string;
  last_name: string;
  function: string;
}

export const useProfileSubmit = (
  setCurrentStep: (step: OnboardingStep) => void,
  hasSurvey: boolean,
  onSuccess?: () => void,
  // Convidado (já tem org): sem survey, o perfil é o último step (bug 84095)
  finishAfterProfile: boolean = false,
) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (values: ProfileData, avatarFile: File | null) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (!user?.id) {
        throw new Error("User ID not found");
      }

      // Atualizar perfil
      const fullName = `${values.first_name} ${values.last_name}`.trim();

      // v2: self-update é PUT /users/me; cargo vai em `position` (o legacy
      // dropava `function` silenciosamente — não existe no UpdateProfileDTO)
      await put("/users/me", {
        full_name: fullName,
        position: values.function,
      });

      // Se houver avatar, fazer upload
      if (avatarFile) {
        try {
          const formData = new FormData();
          formData.append("avatar", avatarFile, avatarFile.name);

          await post(`/users/${user.id}/avatar`, formData, {
            parseJson: true,
            headers: {},
          });
        } catch (avatarError) {
          // Não bloqueia o fluxo se o upload do avatar falhar — o perfil já foi
          // salvo. Mas sem registro não havia como saber que o avatar não subiu.
          logger.error(
            "[useProfileSubmit] Falha no upload do avatar (fluxo segue)",
            avatarError,
          );
        }
      }

      // Mark step as completed
      if (onSuccess) {
        onSuccess();
      }

      // Avançar para survey se houver configuração; sem survey, convidado
      // conclui aqui e os demais vão para a criação de organização
      if (hasSurvey) {
        setCurrentStep("survey");
      } else if (finishAfterProfile) {
        await finishInvitedOnboarding();
        return;
      } else {
        setCurrentStep("organization");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message || t("auth.onboarding.errors.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    onSubmit,
    isSubmitting,
    error,
    setError,
  };
};
